import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect } from "react";

function colorFor(score) {
    if (score <= 40) return "#ef4444";
    if (score <= 70) return "#f97316";
    return "#22c55e";
}

export default function ScoreCircle({ score = 0, size = 240, label = "Identity Score" }) {
    const radius = size / 2 - 14;
    const circumference = 2 * Math.PI * radius;
    const target = Math.max(0, Math.min(100, score));
    const color = colorFor(target);

    const count = useMotionValue(0);
    const rounded = useTransform(count, (v) => Math.round(v));
    const dash = useTransform(count, (v) => circumference - (v / 100) * circumference);

    useEffect(() => {
        const controls = animate(count, target, {
            duration: 1.6,
            ease: [0.16, 1, 0.3, 1],
        });
        return controls.stop;
    }, [target, count]);

    return (
        <div
            className="relative flex items-center justify-center"
            style={{ width: size, height: size }}
            data-testid="score-circle"
        >
            <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                    boxShadow: `0 0 50px ${color}55, inset 0 0 22px ${color}11`,
                }}
                animate={{ opacity: [0.5, 0.9, 0.5] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            />
            <svg width={size} height={size} className="-rotate-90 relative z-10">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="rgba(124,58,237,0.10)"
                    strokeWidth="10"
                    fill="none"
                />
                <motion.circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth="10"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    style={{ strokeDashoffset: dash, filter: `drop-shadow(0 0 8px ${color}aa)` }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <motion.span
                    className="font-syne text-6xl font-bold tracking-tight"
                    style={{ color }}
                    data-testid="score-circle-value"
                >
                    {rounded}
                </motion.span>
                <span className="font-jetbrains-mono text-[11px] uppercase tracking-[0.2em] text-aura-muted mt-1">
                    / 100
                </span>
                <span className="font-dm-sans text-xs text-aura-body mt-2">{label}</span>
            </div>
        </div>
    );
}

